import React, { useMemo, useState } from "react"
import Typography from "aldoo-ra/Typography"
import { usePromise } from "aldoo-ra/usePromise"
import { formatBytes, formatNumber } from "aldoo-ra/Admin/KPI/format"
import {
  ResponsiveContainer,
  Tooltip,
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts"
import {
  MagnifyingGlassIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline"
import { GetStats } from "./api"

const COLORS = [
  "#6366f1", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444",
  "#8b5cf6", "#ec4899", "#14b8a6", "#f97316", "#84cc16",
  "#06b6d4", "#a855f7", "#eab308", "#22c55e", "#64748b",
]

const Card = ({ label, value, sub }) => (
  <div className="rounded-lg border border-admin_bordered bg-admin_paper p-4 flex flex-col gap-1">
    <Typography variant="admin_caption" className="text-admin_text_secondary uppercase tracking-wide">
      {label}
    </Typography>
    <Typography variant="admin_h4" className="font-semibold text-admin_text">
      {value}
    </Typography>
    {sub ? (
      <Typography variant="admin_caption" className="text-admin_text_secondary">
        {sub}
      </Typography>
    ) : null}
  </div>
)

// File-extension breakdown as a horizontal bar chart. Sortable by count or size
// (the active sort key also drives the bar length), with a text filter.
const ExtensionBars = ({ extensions }) => {
  const [search, setSearch] = useState("")
  const [sort, setSort] = useState({ key: "count", dir: -1 })

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: -s.dir } : { key, dir: -1 }))

  const data = useMemo(() => {
    const term = search.trim().toLowerCase()
    const filtered = term
      ? extensions.filter((e) => (e.ext || "").toLowerCase().includes(term))
      : extensions
    const sorted = [...filtered].sort((a, b) =>
      sort.key === "size" ? (a.bytes - b.bytes) * sort.dir : (a.count - b.count) * sort.dir
    )
    return sorted.map((e) => ({
      name: e.ext === "(none)" ? "no ext" : e.ext,
      count: e.count,
      bytes: e.bytes,
    }))
  }, [extensions, search, sort])

  const metric = sort.key === "size" ? "bytes" : "count"
  const height = Math.max(160, data.length * 32 + 16)

  const SortBtn = ({ label, k }) => {
    const active = sort.key === k
    return (
      <button
        className={`inline-flex items-center gap-1 px-2 py-1 rounded ${active ? "bg-admin_primary text-admin_text_inv" : "text-admin_text_secondary"}`}
        onClick={() => toggleSort(k)}
      >
        {label}
        {active &&
          (sort.dir === 1 ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}
      </button>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[160px]">
          <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-admin_text_secondary" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search extension…"
            className="w-full pl-9 pr-3 py-2 border border-admin_bordered rounded-lg text-sm bg-admin_neutral text-admin_text placeholder-admin_text_secondary focus:outline-none focus:border-admin_primary"
          />
        </div>
        <div className="flex gap-1 text-sm">
          <span className="self-center text-admin_text_secondary mr-1">Sort:</span>
          <SortBtn label="Count" k="count" />
          <SortBtn label="Size" k="size" />
        </div>
      </div>

      {data.length === 0 ? (
        <Typography variant="admin_body" className="text-admin_text_secondary py-6">
          No extensions match "{search}".
        </Typography>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 4, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
            <XAxis
              type="number"
              tick={{ fontSize: 12 }}
              tickFormatter={(v) => (metric === "bytes" ? formatBytes(v) : formatNumber(v))}
            />
            <YAxis type="category" dataKey="name" width={70} tick={{ fontSize: 12 }} />
            <Tooltip
              formatter={(_, __, p) =>
                metric === "bytes"
                  ? [formatBytes(p.payload.bytes), `${p.payload.name} (${formatNumber(p.payload.count)} files)`]
                  : [`${formatNumber(p.payload.count)} files`, `${p.payload.name} (${formatBytes(p.payload.bytes)})`]
              }
            />
            <Bar dataKey={metric} radius={[0, 4, 4, 0]}>
              {data.map((_, i) => (
                <Cell key={i} fill={COLORS[i % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  )
}

const BucketChart = ({ buckets }) => (
  <ResponsiveContainer width="100%" height={300}>
    <BarChart data={buckets} margin={{ top: 10, right: 10, left: 0, bottom: 30 }}>
      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
      <XAxis dataKey="label" angle={-20} textAnchor="end" height={60} tick={{ fontSize: 12 }} />
      <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
      <Tooltip
        formatter={(v, _, p) => [`${formatNumber(v)} files`, `${formatBytes(p.payload.bytes)} total`]}
      />
      <Bar dataKey="count" radius={[4, 4, 0, 0]}>
        {buckets.map((_, i) => (
          <Cell key={i} fill={COLORS[i % COLORS.length]} />
        ))}
      </Bar>
    </BarChart>
  </ResponsiveContainer>
)

const StatsSection = () => {
  const { pending, result } = usePromise(GetStats)

  if (pending && !result) {
    return (
      <Typography variant="admin_body" className="text-admin_text_secondary py-6">
        Loading statistics…
      </Typography>
    )
  }
  if (!result || result.error) {
    return (
      <Typography variant="admin_body" className="text-admin_text_secondary py-6">
        No statistics available.
      </Typography>
    )
  }

  const { totalOrders, totalFiles, totalBytes, sizeStats, modeBucket, buckets, extensions, statusCounts } = result
  const statusLine = Object.entries(statusCounts || {})
    .map(([k, v]) => `${v} ${k}`)
    .join(" · ")

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card label="Orders" value={formatNumber(totalOrders)} sub={statusLine} />
        <Card label="Files" value={formatNumber(totalFiles)} />
        <Card label="Total size" value={formatBytes(totalBytes)} />
        <Card label="Average size" value={formatBytes(sizeStats.avg)} />
        <Card label="Smallest" value={formatBytes(sizeStats.min)} />
        <Card label="Largest" value={formatBytes(sizeStats.max)} />
        <Card label="Median" value={formatBytes(sizeStats.median)} />
        <Card label="Most common range" value={modeBucket.label || "--"} sub={modeBucket.count ? `${formatNumber(modeBucket.count)} files` : ""} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-lg border border-admin_bordered bg-admin_paper p-4">
          <Typography variant="admin_h5" className="font-semibold text-admin_text mb-2">File extensions</Typography>
          <ExtensionBars extensions={extensions} />
        </div>

        <div className="rounded-lg border border-admin_bordered bg-admin_paper p-4">
          <Typography variant="admin_h5" className="font-semibold mb-2 text-admin_text">File size distribution</Typography>
          <BucketChart buckets={buckets} />
        </div>
      </div>
    </div>
  )
}

export default StatsSection
